import React, { useState, useEffect } from 'react';
import { Crown, TrendingUp, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { getCustomerId } from '@/utils/customerIdentifier';

interface CustomerLoyaltyWidgetProps {
  restaurantId: string;
  currency?: string;
  className?: string;
}

interface CustomerLoyalty {
  current_points: number;
  total_earned_points: number;
  total_redeemed_points: number;
}

interface LoyaltyProgram {
  id: string;
  name: string;
  points_per_dollar: number;
  min_points_to_redeem: number;
  point_value: number;
  is_active: boolean;
}

export const CustomerLoyaltyWidget: React.FC<CustomerLoyaltyWidgetProps> = ({ 
  restaurantId,
  currency = 'USD', 
  className = "" 
}) => {
  const [loyaltyData, setLoyaltyData] = useState<CustomerLoyalty | null>(null);
  const [loyaltyProgram, setLoyaltyProgram] = useState<LoyaltyProgram | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyaltyData();
  }, [restaurantId]);

  const fetchLoyaltyData = async () => {
    try {
      const customerId = getCustomerId();

      // Fetch loyalty program details
      const { data: programData } = await supabase
        .from('loyalty_programs')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .single();

      if (programData) {
        setLoyaltyProgram(programData);

        // Fetch customer loyalty points
        const { data: loyaltyData } = await supabase
          .from('customer_loyalty_points')
          .select('*')
          .eq('customer_id', customerId)
          .eq('restaurant_id', restaurantId)
          .single();

        if (loyaltyData) {
          setLoyaltyData(loyaltyData);
        } else {
          // Create initial loyalty record for new customer
          const { data: newLoyalty } = await supabase
            .from('customer_loyalty_points')
            .insert({
              customer_id: customerId,
              restaurant_id: restaurantId,
              current_points: 0,
              total_earned_points: 0,
              total_redeemed_points: 0
            })
            .select()
            .single();

          if (newLoyalty) {
            setLoyaltyData(newLoyalty);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierInfo = (totalPoints: number) => {
    if (totalPoints >= 1000) return { tier: 'Gold', icon: Crown, color: 'text-yellow-500' };
    if (totalPoints >= 500) return { tier: 'Silver', icon: TrendingUp, color: 'text-gray-400' };
    return { tier: 'Bronze', icon: Gift, color: 'text-orange-500' };
  };

  if (loading || !loyaltyProgram || !loyaltyData) {
    return null;
  }

  const tierInfo = getTierInfo(loyaltyData.total_earned_points);
  const TierIcon = tierInfo.icon;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`flex items-center gap-2 bg-white text-gray-700 border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-full ${className}`}
        >
          <TierIcon className={`h-4 w-4 ${tierInfo.color}`} />
          <span className="font-medium text-sm">{loyaltyData.current_points}</span>
          <span className="text-xs text-gray-500">pts</span>
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TierIcon className={`h-5 w-5 ${tierInfo.color}`} />
            {loyaltyProgram.name || 'Loyalty Program'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">
              {loyaltyData.current_points}
            </div>
            <div className="text-sm text-gray-600">Current Points</div>
            <div className={`text-sm font-medium ${tierInfo.color}`}>
              {tierInfo.tier} Member
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-semibold text-green-600">
                {loyaltyData.total_earned_points}
              </div>
              <div className="text-xs text-gray-600">Total Earned</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="font-semibold text-blue-600">
                {loyaltyData.total_redeemed_points}
              </div>
              <div className="text-xs text-gray-600">Total Redeemed</div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3">
            <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Earn {loyaltyProgram.points_per_dollar} point per {currency} 1 spent</li>
              <li>• Redeem {loyaltyProgram.min_points_to_redeem} points = {currency} {(loyaltyProgram.point_value * loyaltyProgram.min_points_to_redeem).toFixed(2)}</li>
              <li>• Points never expire</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 